import { DEFAULT_SETTINGS } from './settingsStore';
import type {
  MuteEntry, NotificationLevel, NotificationRule, NotificationSettings, QuietHours,
} from './types';

/**
 * `GET /notifications/settings` answers camelCase since 055. It used to hand
 * back the RAW Postgres row (snake_case) when a row existed and a camelCase
 * default object when it did not, so this mapper still reads BOTH spellings —
 * a client deployed against an older API must not blank the settings screen.
 * Anything missing falls back to the client default.
 */
type Wire = Record<string, unknown>;

const bool = (row: Wire, camel: string, snake: string, fallback: boolean): boolean => {
  const value = row[camel] ?? row[snake];
  return typeof value === 'boolean' ? value : fallback;
};

const level = (row: Wire, camel: string, snake: string, fallback: NotificationLevel): NotificationLevel => {
  const value = row[camel] ?? row[snake];
  return value === 'all' || value === 'mentions' || value === 'none' ? value : fallback;
};

const text = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value !== '' ? value.slice(0, 5) : fallback;

function toQuietHours(row: Wire): QuietHours {
  const nested = (row.quietHours ?? {}) as Wire;
  const base = DEFAULT_SETTINGS.quietHours;
  const days = nested.days ?? row.quiet_days;
  return {
    enabled: bool({ ...nested, ...row }, 'enabled', 'quiet_hours_enabled', base.enabled),
    start: text(nested.start ?? row.quiet_start, base.start),
    end: text(nested.end ?? row.quiet_end, base.end),
    timezone: typeof (nested.timezone ?? row.quiet_timezone) === 'string'
      ? String(nested.timezone ?? row.quiet_timezone)
      : base.timezone,
    days: Array.isArray(days) ? days.filter((day): day is number => typeof day === 'number') : [],
    allowUrgent: bool({ ...nested, ...row }, 'allowUrgent', 'quiet_allow_urgent', base.allowUrgent),
    allowCalls: bool({ ...nested, ...row }, 'allowCalls', 'quiet_allow_calls', base.allowCalls),
  };
}

/**
 * `notification_settings.per_kind` is a CHANNEL MATRIX on the wire —
 * `{"mention":{"inapp":true,"push":false}}` — while the client models one level
 * per kind. Passing the raw object straight through typed it as a level, and
 * `decideDelivery` then compared an object to `'all'`, failed, and suppressed
 * EVERY notification of every kind the user had ever touched in Settings.
 */
function toPerKind(raw: unknown): NotificationSettings['perKind'] {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: Record<string, NotificationLevel> = {};
  for (const [kind, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value === 'all' || value === 'mentions' || value === 'none') {
      out[kind] = value;
      continue;
    }
    if (typeof value === 'object' && value !== null) {
      const inapp = (value as { inapp?: unknown }).inapp;
      out[kind] = inapp === false ? 'none' : 'all';
    }
  }
  return out as NotificationSettings['perKind'];
}

/**
 * THE MUTE LEDGER. `settings.mutes` was never hydrated from anywhere, so the
 * client-side mute branch tested an array that was permanently empty and the
 * Settings → Muted list said "Nothing is muted" however many things you had
 * muted. The server now returns the live ledger, name included, because a row
 * reading "Muted · <uuid>" is not a list anyone can act on.
 */
export function toMutes(raw: unknown): MuteEntry[] {
  if (!Array.isArray(raw)) return [];
  const out: MuteEntry[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const row = item as Record<string, unknown>;
    const scopeKind = row.scopeKind;
    const scopeId = row.scopeId;
    if (typeof scopeKind !== 'string' || typeof scopeId !== 'string') continue;
    const until = row.until;
    out.push({
      scopeKind: scopeKind as MuteEntry['scopeKind'],
      scopeId,
      scopeName: typeof row.scopeName === 'string' ? row.scopeName : 'Untitled',
      until: typeof until === 'string' ? Date.parse(until) : null,
      allowMentions: row.allowMentions === true,
    });
  }
  return out;
}

/** Keyword alerts. Persisted server-side since 055; before that they were lost on reload. */
export function toRules(raw: unknown): NotificationRule[] {
  if (!Array.isArray(raw)) return [];
  const out: NotificationRule[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const row = item as Record<string, unknown>;
    if (typeof row.id !== 'string') continue;
    out.push({
      id: row.id,
      name: typeof row.name === 'string' ? row.name : 'Rule',
      enabled: row.enabled !== false,
      priority: typeof row.priority === 'number' ? row.priority : 100,
      match: (row.match ?? {}) as NotificationRule['match'],
      action: (row.action ?? {}) as NotificationRule['action'],
    });
  }
  return out;
}

export function toNotificationSettings(row: Wire): NotificationSettings {
  const perKind = toPerKind(row.perKind ?? row.per_kind);
  return {
    ...DEFAULT_SETTINGS,
    mutes: toMutes(row.mutes),
    rules: toRules(row.rules),
    pushSupported: row.pushSupported === true,
    vapidPublicKey: typeof row.vapidPublicKey === 'string' ? row.vapidPublicKey : null,
    inappEnabled: bool(row, 'inappEnabled', 'inapp_enabled', DEFAULT_SETTINGS.inappEnabled),
    pushEnabled: bool(row, 'pushEnabled', 'push_enabled', DEFAULT_SETTINGS.pushEnabled),
    soundEnabled: bool(row, 'soundEnabled', 'sound_enabled', DEFAULT_SETTINGS.soundEnabled),
    desktopBanner: bool(row, 'desktopBanner', 'desktop_banner', DEFAULT_SETTINGS.desktopBanner),
    showPreview: bool(row, 'showPreview', 'show_preview', DEFAULT_SETTINGS.showPreview),
    perKind,
    defaultTeamLevel: level(row, 'defaultTeamLevel', 'default_team_level', 'mentions'),
    defaultChannelLevel: level(row, 'defaultChannelLevel', 'default_channel_level', 'mentions'),
    defaultChatLevel: level(row, 'defaultChatLevel', 'default_chat_level', 'all'),
    quietHours: toQuietHours(row),
    respectDnd: bool(row, 'respectDnd', 'respect_dnd', DEFAULT_SETTINGS.respectDnd),
  };
}
